import Fuse from 'fuse.js'
import type { IFuseOptions } from 'fuse.js'
import { useEffect, useMemo, useState } from 'react'

type UseSearchParams<T> = {
  items: readonly T[]
  searchTerm: string
  options?: IFuseOptions<T>
  debounceMs?: number
}

const useSearch = <T>({ items, searchTerm, options, debounceMs = 300 }: UseSearchParams<T>) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  const fuse = useMemo(() => new Fuse([...items], options), [items, options])
  const normalizedSearchTerm = debouncedSearchTerm.trim()

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm), debounceMs)
    return () => clearTimeout(timeout)
  }, [debounceMs, searchTerm])

  return useMemo(() => {
    if (!normalizedSearchTerm) {
      return [...items]
    }
    return fuse.search(normalizedSearchTerm).map(result => result.item)
  }, [fuse, items, normalizedSearchTerm])
}

export default useSearch
